import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { SidebarEmptyState } from '@/components/layout/sidebar/SidebarEmptyState'
import { SidebarErrorState } from '@/components/layout/sidebar/SidebarErrorState'
import { SidebarListSkeleton } from '@/components/layout/sidebar/SidebarListSkeleton'
import { fetchWithAuth } from '@/lib/api/core'
import { formatRelativeTime } from '@/lib/format-relative-time'
import { testIds } from '@/lib/test-ids'
import { DriveCommentsList } from './drive-comments-list'
import { resolveOpenExternalIcon } from '@/lib/open-destination-icons'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'

type DriveFileDetails = {
  id: string
  name: string
  mimeType: string
  modifiedTime?: string
  createdTime?: string
  size?: string | null
  webViewLink?: string
  ownedByMe?: boolean
  shared?: boolean
  ownerName?: string | null
  ownerEmail?: string | null
  lastModifyingUser?: string | null
  description?: string | null
  version?: string | null
}

type DriveRevision = {
  id: string
  modifiedTime?: string
  lastModifyingUser?: string | null
  size?: string | null
}

function typeLabel(mime: string): string {
  if (mime.includes('folder')) return 'Folder'
  if (mime.includes('document')) return 'Google Doc'
  if (mime.includes('spreadsheet')) return 'Google Sheet'
  if (mime.includes('presentation')) return 'Google Slides'
  if (mime.includes('pdf')) return 'PDF'
  if (mime.startsWith('image/')) return 'Image'
  if (mime.includes('csv') || mime.endsWith('/csv')) return 'CSV'
  if (mime.includes('shortcut')) return 'Shortcut'
  return mime.split('/').pop() || 'File'
}

function formatBytes(size?: string | null): string {
  const n = Number(size)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-sm text-foreground break-words">{value}</div>
    </div>
  )
}

function DriveFileDetailsBody({
  fileId,
  organizationId,
  fallbackName,
  fallbackMime,
  fallbackLink,
}: {
  fileId: string
  organizationId?: string
  fallbackName: string
  fallbackMime: string
  fallbackLink?: string
}) {
  const [file, setFile] = useState<DriveFileDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const params = organizationId
          ? `?organizationId=${encodeURIComponent(organizationId)}`
          : ''
        const res = await fetchWithAuth(
          `/api/google-drive/files/${encodeURIComponent(fileId)}${params}`,
        )
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as { file?: DriveFileDetails }
        if (!cancelled) setFile(data.file ?? null)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load file details')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [fileId, organizationId])

  if (loading) return <SidebarListSkeleton rows={6} dense />
  if (error) return <SidebarErrorState message={error} />

  const name = file?.name || fallbackName
  const mime = file?.mimeType || fallbackMime
  const link = file?.webViewLink || fallbackLink
  const DriveOpenIcon = resolveOpenExternalIcon(GoogleDriveIcon)

  return (
    <div className="space-y-4 p-4" data-testid={testIds.driveFolderView.previewInfoDetails}>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Title
        </div>
        <h4 className="mt-0.5 text-[1.0625rem] font-semibold leading-snug text-foreground">
          {name}
        </h4>
      </div>
      <MetaRow label="Type" value={typeLabel(mime)} />
      <MetaRow label="Owner" value={file?.ownerName || file?.ownerEmail} />
      <MetaRow
        label="Modified"
        value={
          file?.modifiedTime
            ? `${formatRelativeTime(file.modifiedTime)}${
                file.lastModifyingUser ? ` · ${file.lastModifyingUser}` : ''
              }`
            : null
        }
      />
      <MetaRow
        label="Created"
        value={file?.createdTime ? formatRelativeTime(file.createdTime) : null}
      />
      <MetaRow label="Size" value={formatBytes(file?.size)} />
      <MetaRow
        label="Sharing"
        value={file?.shared ? 'Shared' : file?.ownedByMe === false ? 'Shared with you' : 'Private'}
      />
      {file?.description ? <MetaRow label="Description" value={file.description} /> : null}
      {link ? (
        <Button size="sm" variant="outline" className="w-full justify-start gap-2" asChild>
          <a href={link} target="_blank" rel="noopener noreferrer">
            <DriveOpenIcon className="h-4 w-4" />
            Open in Drive
          </a>
        </Button>
      ) : null}
    </div>
  )
}

function DriveFileHistoryBody({
  fileId,
  organizationId,
}: {
  fileId: string
  organizationId?: string
}) {
  const [revisions, setRevisions] = useState<DriveRevision[]>([])
  const [unsupported, setUnsupported] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const params = organizationId
          ? `?organizationId=${encodeURIComponent(organizationId)}`
          : ''
        const res = await fetchWithAuth(
          `/api/google-drive/files/${encodeURIComponent(fileId)}/revisions${params}`,
        )
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as { revisions?: DriveRevision[]; unsupported?: boolean }
        if (!cancelled) {
          setRevisions(data.revisions ?? [])
          setUnsupported(Boolean(data.unsupported))
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load history')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [fileId, organizationId])

  if (loading) return <SidebarListSkeleton rows={6} dense />
  if (error) return <SidebarErrorState message={error} />
  if (unsupported || revisions.length === 0) {
    return (
      <SidebarEmptyState
        title="No revision history"
        description="Google Drive does not expose version history for this file type."
      />
    )
  }

  return (
    <ul className="p-2" data-testid={testIds.driveFolderView.previewInfoHistory}>
      {revisions.map((rev) => (
        <li
          key={rev.id}
          className="border-b border-border/40 px-3 py-2.5 last:border-b-0"
        >
          <div className="text-sm text-foreground">
            {formatRelativeTime(rev.modifiedTime) || 'Revision'}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {[rev.lastModifyingUser, formatBytes(rev.size)].filter(Boolean).join(' · ')}
          </div>
        </li>
      ))}
    </ul>
  )
}

export function DrivePreviewInfoPane({
  mode,
  fileId,
  organizationId,
  name,
  mimeType,
  webViewLink,
}: {
  mode: 'details' | 'history' | 'comments'
  fileId: string
  organizationId?: string
  name: string
  mimeType: string
  webViewLink?: string
}) {
  if (mode === 'comments') {
    return (
      <div className="h-full overflow-y-auto">
        <DriveCommentsList fileId={fileId} organizationId={organizationId} />
      </div>
    )
  }
  if (mode === 'history') {
    return (
      <div className="h-full overflow-y-auto">
        <DriveFileHistoryBody fileId={fileId} organizationId={organizationId} />
      </div>
    )
  }
  return (
    <div className="h-full overflow-y-auto">
      <DriveFileDetailsBody
        fileId={fileId}
        organizationId={organizationId}
        fallbackName={name}
        fallbackMime={mimeType}
        fallbackLink={webViewLink}
      />
    </div>
  )
}
